import { environment } from '../../environments/environment';
import { HttpParams } from '@angular/common/http';
import { map, tap } from 'rxjs/operators';
import { HttpService } from './http.service';
import { throwError as observableThrowError, Observable, ReplaySubject, of } from 'rxjs';
import { CacheService } from './cache.service';
import { Injectable } from '@angular/core';
import { ShipmentContactsTemplate, ShipmentTemplate, ShipmentWarningsTemplate } from '../models/SCMTemplates';

@Injectable()
export class TemplatesService {
    private _shipmentTemplates$: ReplaySubject<Array<ShipmentTemplate>> = new ReplaySubject(1);
    shipmentTemplates: Array<ShipmentTemplate>;

    private _shipmentContactsTemplates$: ReplaySubject<Array<ShipmentContactsTemplate>> = new ReplaySubject(1);
    shipmentContactsTemplates: Array<ShipmentContactsTemplate>;

    private _shipmentWarningsTemplates$: ReplaySubject<Array<ShipmentWarningsTemplate>> = new ReplaySubject(1);
    shipmentWarningsTemplates: Array<ShipmentWarningsTemplate>;

    get shipmentTemplates$(): Observable<Array<any>> {
        return this._shipmentTemplates$.asObservable();
    }

    get shipmentContactsTemplates$(): Observable<Array<any>> {
        return this._shipmentContactsTemplates$.asObservable();
    }

    get shipmentWarningsTemplates$(): Observable<Array<any>> {
        return this._shipmentWarningsTemplates$.asObservable();
    }

    constructor(private httpService: HttpService, private cacheService: CacheService) {
        this.shipmentTemplates$.subscribe(
            shipmentTemplates => {
                this.shipmentTemplates = shipmentTemplates;
            }
        );

        this.shipmentContactsTemplates$.subscribe(
            shipmentContactsTemplates => {
                this.shipmentContactsTemplates = shipmentContactsTemplates;
            }
        );

        this.shipmentWarningsTemplates$.subscribe(
            shipmentWarningsTemplates => {
                this.shipmentWarningsTemplates = shipmentWarningsTemplates;
            }
        );
    }

    getShipmentTemplates(projectId: number = null): Observable<Array<ShipmentTemplate>> {
        let params = {
            projectId: projectId
        };

        let httpParams: HttpParams = new HttpParams();

        Object.keys(params).forEach((k) => {
            params[k] && (httpParams = httpParams.set(k, params[k]));
        });

        if (this.cacheService.hasItem('shipmentTemplates', 0)) {
            return of(this.cacheService.getItem('shipmentTemplates', 0));
        } else {
            return this.httpService.get<Array<ShipmentTemplate>>(`${environment.apiV1BaseUrl}/supplychains/templates/shipments`, {
                params: httpParams
            }).pipe(
                tap((shipmentTemplates: Array<ShipmentTemplate>) => this.cacheService.setItem('currentCustomerProjects', 0, shipmentTemplates)),
                tap((shipmentTemplates: Array<ShipmentTemplate>) => this._shipmentTemplates$.next(shipmentTemplates))
            );
        }
    }

    getShipmentTemplate(templateId: number): Observable<ShipmentTemplate> {
        if (this.cacheService.hasItem('shipmentTemplate', templateId)) {
            return of(this.cacheService.getItem('shipmentTemplate', templateId));
        } else {
            return this.httpService.get<ShipmentTemplate>(`${environment.apiV1BaseUrl}/supplychains/templates/shipments/${templateId}`).pipe(
                map((shipmentTemplate) => this.cacheService.setItem('shipmentTemplate', templateId, shipmentTemplate))
            );
        }
    }

    getShipmentContactsTemplates(projectId: number = null): Observable<Array<ShipmentContactsTemplate>> {
        let params = {
            projectId: projectId
        };

        let httpParams: HttpParams = new HttpParams();

        Object.keys(params).forEach((k) => {
            params[k] && (httpParams = httpParams.set(k, params[k]));
        });

        if (this.cacheService.hasItem('shipmentContactsTemplates', 0)) {
            return of(this.cacheService.getItem('shipmentContactsTemplates', 0));
        } else {
            return this.httpService.get<Array<ShipmentContactsTemplate>>(`${environment.apiV1BaseUrl}/supplychains/templates/shipment-contacts`, {
                params: httpParams
            }).pipe(
                tap((shipmentContactsTemplates: Array<ShipmentContactsTemplate>) => this.cacheService.setItem('currentCustomerProjects', 0, shipmentContactsTemplates)),
                tap((shipmentContactsTemplates: Array<ShipmentContactsTemplate>) => this._shipmentContactsTemplates$.next(shipmentContactsTemplates))
            );
        }
    }

    getShipmentContactsTemplate(templateId: number): Observable<ShipmentContactsTemplate> {
        if (this.cacheService.hasItem('shipmentContactsTemplate', templateId)) {
            return of(this.cacheService.getItem('shipmentContactsTemplate', templateId));
        } else {
            return this.httpService.get<ShipmentTemplate>(`${environment.apiV1BaseUrl}/supplychains/templates/shipment-contacts/${templateId}`).pipe(
                map((shipmentContactsTemplate) => this.cacheService.setItem('shipmentContactsTemplate', templateId, shipmentContactsTemplate))
            );
        }
    }

    getShipmentWarningsTemplates(projectId: number = null): Observable<Array<ShipmentWarningsTemplate>> {
        let params = {
            projectId: projectId
        };

        let httpParams: HttpParams = new HttpParams();

        Object.keys(params).forEach((k) => {
            params[k] && (httpParams = httpParams.set(k, params[k]));
        });

        if (this.cacheService.hasItem('shipmentWarningsTemplates', 0)) {
            return of(this.cacheService.getItem('shipmentWarningsTemplates', 0));
        } else {
            return this.httpService.get<Array<ShipmentWarningsTemplate>>(`${environment.apiV1BaseUrl}/supplychains/templates/shipment-warnings`, {
                params: httpParams
            }).pipe(
                tap((shipmentWarningsTemplates: Array<ShipmentWarningsTemplate>) => this.cacheService.setItem('currentCustomerProjects', 0, shipmentWarningsTemplates)),
                tap((shipmentWarningsTemplates: Array<ShipmentWarningsTemplate>) => this._shipmentWarningsTemplates$.next(shipmentWarningsTemplates))
            );
        }
    }

    getShipmentWarningsTemplate(templateId: number): Observable<ShipmentWarningsTemplate> {
        if (this.cacheService.hasItem('shipmentWarningsTemplate', templateId)) {
            return of(this.cacheService.getItem('shipmentWarningsTemplate', templateId));
        } else {
            return this.httpService.get<ShipmentTemplate>(`${environment.apiV1BaseUrl}/supplychains/templates/shipment-warnings/${templateId}`).pipe(
                map((shipmentWarningsTemplate) => this.cacheService.setItem('shipmentWarningsTemplate', templateId, shipmentWarningsTemplate))
            );
        }
    }


}
